import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronLeft,
  Save,
  User,
  Phone,
  Mail,
  AlertCircle,
  RefreshCw,
  Trash2,
  Flag,
  AlertTriangle,
  CheckCircle,
  Loader2,
  MapPin
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Select } from '../../components/ui/Input';
import { useApi } from '../../contexts/ApiContext';
import { useSeries } from '../../App';

export default function CompetitorForm() {
  const { seriesSlug, competitorId } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const currentSeries = useSeries();

  const isEditing = !!competitorId;

  // Form state
  const [formData, setFormData] = useState({
    // Personal Info
    first_name: '',
    last_name: '',
    middle_name: '',
    nickname: '',
    email: '',
    mobile_phone: '',
    home_phone: '',
    date_of_birth: '',
    hometown_city: '',
    home_state_ID: '',
    // Equipment
    team_ID: '',
    chassis_ID: '',
    // Series (multi-select)
    wrg_series_IDs: (currentSeries?.racingEntityId || currentSeries?.dbId)
      ? [(currentSeries.racingEntityId || currentSeries.dbId).toString()]
      : [],
    // Status
    driver_status_ID: '',
    // Stats
    starts: '',
    wins: '',
    last_year_raced: '',
    // Emergency Contact
    emergency_first_name: '',
    emergency_last_name: '',
    emergency_phone: '',
    emergency_contact_relationship: '',
    // Profile
    biography: '',
    headshot_url: ''
  });

  // UI state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // Duplicate detection state
  const [duplicates, setDuplicates] = useState([]);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [duplicateConfirmed, setDuplicateConfirmed] = useState(false);

  const { data: dropdownData } = useQuery({
    queryKey: ['competitor-dropdown-data'],
    queryFn: async () => {
      const [teamsRes, chassisRes, seriesRes, statusesRes, statesRes] = await Promise.all([
        api.drivers.getDropdownData('teams'),
        api.drivers.getDropdownData('chassis'),
        api.drivers.getDropdownData('series'),
        api.drivers.getDropdownData('statuses'),
        api.drivers.getDropdownData('states')
      ]);
      return {
        teams: Array.isArray(teamsRes.data) ? teamsRes.data : [],
        chassis: Array.isArray(chassisRes.data) ? chassisRes.data : [],
        seriesList: Array.isArray(seriesRes.data) ? seriesRes.data : [],
        statuses: Array.isArray(statusesRes.data) ? statusesRes.data : [],
        states: Array.isArray(statesRes.data) ? statesRes.data : []
      };
    },
  });

  const teams = dropdownData?.teams || [];
  const chassis = dropdownData?.chassis || [];
  const seriesList = dropdownData?.seriesList || [];
  const states = dropdownData?.states || [];
  const statuses = dropdownData?.statuses || [];

  const toggleSeries = (id) => {
    const idStr = id.toString();
    setFormData(prev => ({
      ...prev,
      wrg_series_IDs: prev.wrg_series_IDs.includes(idStr)
        ? prev.wrg_series_IDs.filter(x => x !== idStr)
        : [...prev.wrg_series_IDs, idStr]
    }));
  };

  const { isLoading: loading } = useQuery({
    queryKey: ['competitor-edit', competitorId],
    queryFn: async () => {
      const result = await api.drivers.getById(competitorId);
      if (!result.success || !result.data) throw new Error(result.message || 'Failed to load competitor');
      const driver = result.data.driver || result.data;
      setFormData({
        first_name: driver.first_name || '',
        last_name: driver.last_name || '',
        middle_name: driver.middle_name || '',
        nickname: driver.nickname || '',
        email: driver.email || '',
        mobile_phone: driver.mobile_phone || '',
        home_phone: driver.home_phone || '',
        date_of_birth: driver.date_of_birth ? driver.date_of_birth.slice(0, 10) : '',
        hometown_city: driver.city || '',
        home_state_ID: driver.residence_state_ID?.toString() || '',
        team_ID: driver.team_ID?.toString() || '',
        chassis_ID: driver.chassis_ID?.toString() || '',
        wrg_series_IDs: driver.series_ids
          ? driver.series_ids.split(',').filter(Boolean)
          : [],
        driver_status_ID: driver.driver_status_ID?.toString() || '',
        starts: driver.starts?.toString() || '',
        wins: driver.wins?.toString() || '',
        last_year_raced: driver.last_year_raced?.toString() || '',
        emergency_first_name: driver.emergency_first_name || '',
        emergency_last_name: driver.emergency_last_name || '',
        emergency_phone: driver.emergency_phone || '',
        emergency_contact_relationship: driver.emergency_contact_relationship || '',
        biography: driver.biography || '',
        headshot_url: driver.headshot_url || ''
      });
      return result.data;
    },
    enabled: isEditing,
  });

  // Check for duplicate drivers (debounced)
  useEffect(() => {
    if (isEditing) return;

    const firstName = formData.first_name.trim();
    const lastName = formData.last_name.trim();

    if (!firstName || !lastName || firstName.length < 2 || lastName.length < 2) {
      setDuplicates([]);
      setCheckingDuplicates(false);
      return;
    }

    setCheckingDuplicates(true);

    const timeoutId = setTimeout(async () => {
      try {
        const result = await api.drivers.checkDuplicate(firstName, lastName);
        if (result.success && result.data) {
          setDuplicates(result.data.duplicates || []);
          if (result.data.duplicates?.length > 0) {
            setDuplicateConfirmed(false);
          }
        }
      } catch (err) {
        console.error('Error checking duplicates:', err);
        setDuplicates([]);
      } finally {
        setCheckingDuplicates(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.first_name, formData.last_name, isEditing]);

  // Handle input changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: null }));
    }
    // Reset duplicate confirmation when name changes
    if (field === 'first_name' || field === 'last_name') {
      setDuplicateConfirmed(false);
    }
  };

  // Validate form
  const validate = () => {
    const errors = {};
    if (!formData.first_name.trim()) errors.first_name = 'First name is required';
    if (!formData.last_name.trim()) errors.last_name = 'Last name is required';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    // Block submission if duplicates exist and not confirmed
    if (!isEditing && duplicates.length > 0 && !duplicateConfirmed) {
      errors.duplicate = 'Please confirm this is not a duplicate driver';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    setError(null);

    try {
      const submitData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        middle_name: formData.middle_name || null,
        nickname: formData.nickname || null,
        email: formData.email || null,
        mobile_phone: formData.mobile_phone || null,
        home_phone: formData.home_phone || null,
        date_of_birth: formData.date_of_birth || null,
        hometown_city: formData.hometown_city || null,
        home_state_ID: formData.home_state_ID ? parseInt(formData.home_state_ID) : null,
        team_ID: formData.team_ID ? parseInt(formData.team_ID) : null,
        chassis_ID: formData.chassis_ID ? parseInt(formData.chassis_ID) : null,
        wrg_series_IDs: formData.wrg_series_IDs.map(id => parseInt(id)).filter(Number.isInteger),
        driver_status_ID: formData.driver_status_ID ? parseInt(formData.driver_status_ID) : null,
        starts: formData.starts ? parseInt(formData.starts) : null,
        wins: formData.wins ? parseInt(formData.wins) : null,
        last_year_raced: formData.last_year_raced ? parseInt(formData.last_year_raced) : null,
        emergency_first_name: formData.emergency_first_name || null,
        emergency_last_name: formData.emergency_last_name || null,
        emergency_phone: formData.emergency_phone || null,
        emergency_contact_relationship: formData.emergency_contact_relationship || null,
        biography: formData.biography || null,
        headshot_url: formData.headshot_url || null
      };

      let result;
      if (isEditing) {
        result = await api.drivers.update(competitorId, submitData);
      } else {
        result = await api.drivers.create(submitData);
      }

      if (result.success) {
        const newId = isEditing ? competitorId : result.data?.driver?.driver_ID || result.data?.id;
        navigate(`/${seriesSlug}/competitors/${newId}`);
      } else {
        setError(result.message || 'Failed to save competitor');
      }
    } catch (err) {
      setError('Failed to save competitor');
      console.error('Error saving competitor:', err);
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this competitor? This action cannot be undone.')) {
      return;
    }

    setSaving(true);
    try {
      const result = await api.drivers.delete(competitorId);
      if (result.success) {
        navigate(`/${seriesSlug}/competitors`);
      } else {
        setError(result.message || 'Failed to delete competitor');
      }
    } catch (err) {
      setError('Failed to delete competitor');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-fade-in">
        <div className="page-header">
          <div className="h-8 w-48 bg-rms-dark-600 rounded animate-pulse" />
        </div>
        <Card className="animate-pulse">
          <CardContent className="p-6">
            <div className="space-y-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-12 bg-rms-dark-600 rounded" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Page Header */}
      <div className="page-header">
        <div className="flex items-center gap-4 mb-4">
          <Link to={isEditing ? `/${seriesSlug}/competitors/${competitorId}` : `/${seriesSlug}/competitors`}>
            <Button variant="ghost" size="sm" leftIcon={ChevronLeft}>
              {isEditing ? 'Back to Competitor' : 'Competitors'}
            </Button>
          </Link>
        </div>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="page-title">
              {isEditing ? 'Edit Competitor' : 'Add Competitor'}
            </h1>
            <p className="page-subtitle">
              {isEditing ? 'Update competitor information' : 'Register a new driver'}
            </p>
          </div>
          {isEditing && (
            <Button
              variant="ghost"
              onClick={handleDelete}
              disabled={saving}
              className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Competitor
            </Button>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <Card className="mb-6 border-red-500/50 bg-red-500/10">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-400">{error}</span>
          </CardContent>
        </Card>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Personal Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Personal Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label={<>First Name <span className="text-red-500">*</span></>}
                    placeholder="John"
                    value={formData.first_name}
                    onChange={(e) => handleChange('first_name', e.target.value)}
                    error={validationErrors.first_name}
                    required
                  />

                  <Input
                    label={<>Last Name <span className="text-red-500">*</span></>}
                    placeholder="Smith"
                    value={formData.last_name}
                    onChange={(e) => handleChange('last_name', e.target.value)}
                    error={validationErrors.last_name}
                    required
                  />
                </div>

                {/* Duplicate Warning */}
                {!isEditing && (checkingDuplicates || duplicates.length > 0) && (
                  <div className={`p-4 rounded-lg border ${
                    duplicateConfirmed
                      ? 'bg-emerald-50 border-emerald-200'
                      : 'bg-amber-50 border-amber-200'
                  }`}>
                    {checkingDuplicates ? (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm">Checking for existing drivers...</span>
                      </div>
                    ) : duplicates.length > 0 ? (
                      <div>
                        <div className="flex items-start gap-2 mb-3">
                          {duplicateConfirmed ? (
                            <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          ) : (
                            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                          )}
                          <div>
                            <p className={`font-medium ${duplicateConfirmed ? 'text-emerald-800' : 'text-amber-800'}`}>
                              {duplicateConfirmed
                                ? 'Confirmed as new driver'
                                : `Possible duplicate${duplicates.length > 1 ? 's' : ''} found`}
                            </p>
                            {!duplicateConfirmed && (
                              <p className="text-sm text-amber-700 mt-1">
                                A driver with this name already exists. Please verify this is a different person.
                              </p>
                            )}
                          </div>
                        </div>

                        {!duplicateConfirmed && (
                          <>
                            <div className="space-y-2 mb-3">
                              {duplicates.map(dup => (
                                <div
                                  key={dup.driver_ID}
                                  className="flex items-center justify-between p-3 bg-white rounded border border-amber-200"
                                >
                                  <div>
                                    <p className="font-medium text-slate-800">
                                      {dup.first_name} {dup.middle_name ? `${dup.middle_name} ` : ''}{dup.last_name}
                                    </p>
                                    <div className="flex items-center gap-3 text-sm text-slate-500">
                                      {(dup.hometown || dup.home_state) && (
                                        <span className="flex items-center gap-1">
                                          <MapPin className="w-3 h-3" />
                                          {dup.hometown}{dup.hometown && dup.home_state && ', '}{dup.home_state}
                                        </span>
                                      )}
                                      {dup.team_name && (
                                        <span>{dup.team_name}</span>
                                      )}
                                    </div>
                                  </div>
                                  <a
                                    href={`/${seriesSlug}/competitors/${dup.driver_ID}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-wrg-blue-600 hover:underline"
                                  >
                                    View Profile
                                  </a>
                                </div>
                              ))}
                            </div>

                            <button
                              type="button"
                              onClick={() => setDuplicateConfirmed(true)}
                              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-amber-800 bg-amber-100 hover:bg-amber-200 rounded transition-colors"
                            >
                              <CheckCircle className="w-4 h-4" />
                              This is a different person - continue adding
                            </button>
                          </>
                        )}
                      </div>
                    ) : null}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    label="Middle Name"
                    placeholder="(Optional)"
                    value={formData.middle_name}
                    onChange={(e) => handleChange('middle_name', e.target.value)}
                  />

                  <Input
                    label="Nickname"
                    placeholder='e.g., "The Kid"'
                    value={formData.nickname}
                    onChange={(e) => handleChange('nickname', e.target.value)}
                  />

                  <Input
                    label="Date of Birth"
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => handleChange('date_of_birth', e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Hometown"
                    placeholder="City"
                    value={formData.hometown_city}
                    onChange={(e) => handleChange('hometown_city', e.target.value)}
                  />

                  <Select
                    label="State"
                    value={formData.home_state_ID}
                    onChange={(e) => handleChange('home_state_ID', e.target.value)}
                  >
                    <option value="">Select state...</option>
                    {states.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.name} ({s.abbreviation})
                      </option>
                    ))}
                  </Select>
                </div>

                <Input
                  label="Headshot URL"
                  type="url"
                  placeholder="https://..."
                  value={formData.headshot_url}
                  onChange={(e) => handleChange('headshot_url', e.target.value)}
                />
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Input
                  label="Email"
                  type="email"
                  placeholder="driver@example.com"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  error={validationErrors.email}
                  leftIcon={Mail}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Mobile Phone"
                    placeholder="(555) 123-4567"
                    value={formData.mobile_phone}
                    onChange={(e) => handleChange('mobile_phone', e.target.value)}
                  />

                  <Input
                    label="Home Phone"
                    placeholder="(555) 987-6543"
                    value={formData.home_phone}
                    onChange={(e) => handleChange('home_phone', e.target.value)}
                  />
                </div>

              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  Emergency Contact
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="First Name"
                    placeholder="Jane"
                    value={formData.emergency_first_name}
                    onChange={(e) => handleChange('emergency_first_name', e.target.value)}
                  />

                  <Input
                    label="Last Name"
                    placeholder="Smith"
                    value={formData.emergency_last_name}
                    onChange={(e) => handleChange('emergency_last_name', e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Phone"
                    placeholder="(555) 123-4567"
                    value={formData.emergency_phone}
                    onChange={(e) => handleChange('emergency_phone', e.target.value)}
                  />

                  <Input
                    label="Relationship"
                    placeholder="e.g., Spouse, Parent, Sibling"
                    value={formData.emergency_contact_relationship}
                    onChange={(e) => handleChange('emergency_contact_relationship', e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Racing Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Flag className="w-5 h-5" />
                  Racing Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Series
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {seriesList.map(series => {
                      const idStr = series.wrg_series_ID.toString();
                      const checked = formData.wrg_series_IDs.includes(idStr);
                      return (
                        <label
                          key={series.wrg_series_ID}
                          className="flex items-center gap-2 px-3 py-2 bg-rms-dark-700 border border-slate-600 rounded-lg cursor-pointer hover:border-slate-500"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleSeries(series.wrg_series_ID)}
                            className="rounded border-slate-500"
                          />
                          <span className="text-sm text-slate-100">{series.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <Select
                  label="Status"
                  value={formData.driver_status_ID}
                  onChange={(e) => handleChange('driver_status_ID', e.target.value)}
                >
                  <option value="">Select status...</option>
                  {statuses.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Select>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Select
                    label="Team"
                    value={formData.team_ID}
                    onChange={(e) => handleChange('team_ID', e.target.value)}
                  >
                    <option value="">Select team...</option>
                    {teams.map(team => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </Select>

                  <Select
                    label="Chassis"
                    value={formData.chassis_ID}
                    onChange={(e) => handleChange('chassis_ID', e.target.value)}
                  >
                    <option value="">Select chassis...</option>
                    {chassis.map(ch => (
                      <option key={ch.id} value={ch.id}>
                        {ch.name}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    type="number"
                    label="Starts"
                    placeholder="0"
                    value={formData.starts}
                    onChange={(e) => handleChange('starts', e.target.value)}
                    min="0"
                  />

                  <Input
                    type="number"
                    label="Wins"
                    placeholder="0"
                    value={formData.wins}
                    onChange={(e) => handleChange('wins', e.target.value)}
                    min="0"
                  />

                  <Input
                    type="number"
                    label="Last Year Raced"
                    placeholder={new Date().getFullYear().toString()}
                    value={formData.last_year_raced}
                    onChange={(e) => handleChange('last_year_raced', e.target.value)}
                    min="1900"
                    max={new Date().getFullYear() + 1}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Biography */}
            <Card>
              <CardHeader>
                <CardTitle>Biography</CardTitle>
              </CardHeader>
              <CardContent>
                <textarea
                  className="w-full px-3 py-2 bg-rms-dark-700 border border-slate-600 rounded-lg
                    text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2
                    focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={6}
                  placeholder="Driver biography, accomplishments, background..."
                  value={formData.biography}
                  onChange={(e) => handleChange('biography', e.target.value)}
                />
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  type="submit"
                  className="w-full"
                  disabled={saving}
                  leftIcon={saving ? RefreshCw : Save}
                >
                  {saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Add Competitor')}
                </Button>
                <Link
                  to={isEditing ? `/${seriesSlug}/competitors/${competitorId}` : `/${seriesSlug}/competitors`}
                  className="block"
                >
                  <Button type="button" variant="ghost" className="w-full">
                    Cancel
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Profile Preview */}
            {(formData.first_name || formData.last_name) && (
              <Card>
                <CardHeader>
                  <CardTitle>Preview</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3 p-3 bg-rms-dark-700 rounded-lg">
                    {formData.headshot_url ? (
                      <img
                        src={formData.headshot_url}
                        alt="Preview"
                        className="w-12 h-12 rounded-lg object-cover bg-rms-dark-600"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-rms-dark-600 flex items-center justify-center">
                        <User className="w-6 h-6 text-slate-500" />
                      </div>
                    )}
                    <div>
                      <div className="font-medium text-slate-100">
                        {formData.first_name} {formData.last_name}
                      </div>
                      {formData.nickname && (
                        <div className="text-sm text-slate-400">"{formData.nickname}"</div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Stats Preview */}
            {(formData.starts || formData.wins) && (
              <Card>
                <CardHeader>
                  <CardTitle>Stats</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-3 bg-rms-dark-700 rounded-lg">
                      <div className="text-2xl font-bold text-slate-100">{formData.starts || 0}</div>
                      <div className="text-sm text-slate-400">Starts</div>
                    </div>
                    <div className="text-center p-3 bg-rms-dark-700 rounded-lg">
                      <div className="text-2xl font-bold text-yellow-400">{formData.wins || 0}</div>
                      <div className="text-sm text-slate-400">Wins</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quick Tips */}
            <Card>
              <CardHeader>
                <CardTitle>Tips</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="text-sm text-slate-400 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400">*</span>
                    First and last name are required
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400">*</span>
                    Select a series to associate the driver with
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400">*</span>
                    Stats can be updated automatically from race results
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
}
